import * as React from 'react';

import {NutAnalyticsClient} from 'nutshell-core/nut-analytics-client';

import {BulletedList} from '../../bulleted-list/bulleted-list';
import {colors} from '../../colors/colors';
// @ts-expect-error
import {UpgradeArrowIcon} from '../../icon/svg/upgrade-arrow-icon';
import {Illustration} from '../../illustrations/illustration';
import {StackLegacy} from '../../layout/stack/stack-legacy';
import {Header} from '../../typography/header';
import {Subheader} from '../../typography/subheader';

import {FeatureGateButton} from './feature-gate-button';
import type {FeatureName} from './features-list';
import {getClosingMessage} from './helpers';

import styles from './feature-gate.module.css';

type DetailTextBullet = React.ReactNode | string;

type Props = {
    header: string;
    subHeader: string;
    bullets: DetailTextBullet[];
    isAdmin: boolean;
    feature: FeatureName;
    analyticsClient?: NutAnalyticsClient;
    shouldShowButton?: boolean;
    icon?: any;
    cueCard?: React.ReactNode;
    needLicenses?: boolean;
    noClosingMessage?: boolean;
    buttonText?: string;
    greyBullets?: boolean;
    href?: string;
    upgradePlanName: string | null | undefined;
};

export function FeatureGatePage(props: Props) {
    const {isAdmin, feature, analyticsClient} = props;
    const closingMessage = getClosingMessage(feature, props.upgradePlanName);
    // for sms gates, we don't want to direct user to billing page if the
    // company already has licenses, regardless of if they have access to
    // billing.
    const shouldShowButton =
        typeof props.shouldShowButton === 'boolean' ? props.shouldShowButton : isAdmin;

    React.useEffect(() => {
        if (analyticsClient && feature) {
            analyticsClient.track(analyticsClient.EVENTS.TRACK_FEATURE_GATE_VIEWED, {
                feature,
                isAdmin,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className='full-height full-width flex flex-dir-row gap-48 pad-32 border-box background-offwhite'>
            <div className={styles['left-column']}>
                <StackLegacy spacing={4}>
                    <Header>
                        <span className='flex align-center gap-8 mb-4'>
                            <UpgradeArrowIcon size={25} wrapWithDiv={false} color={colors.blue} />
                            {props.header}
                        </span>
                    </Header>
                    <Subheader>{props.subHeader}</Subheader>
                </StackLegacy>
                <div className='flex gap-48 mt-32 mb-32'>
                    <div className={styles['bullets-wrapper']}>
                        <BulletedList
                            greyBullets={props.greyBullets}
                            bullets={props.bullets}
                            size='big'
                        />
                    </div>
                </div>
                {!props.noClosingMessage && (
                    <div className='text-align-center font-size-normal'>{closingMessage}</div>
                )}
                {shouldShowButton && (
                    <div className='text-align-center pad-16'>
                        <FeatureGateButton
                            variant={props.needLicenses ? 'licenses' : 'upgrade'}
                            text={props.buttonText}
                            href={props.href}
                        />
                    </div>
                )}
            </div>
            <div className={styles['right-column']}>
                <div className={styles['icon-container']}>
                    {props.icon || <Illustration src='bank-safe' />}
                </div>
                {props.cueCard ? props.cueCard : null}
            </div>
        </div>
    );
}
